import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { formatGBP } from "@/lib/marketplace";
import { CheckCircle2, Clock, XCircle, Sparkles, Star } from "lucide-react";
import PostPurchaseRatingModal from "@/components/PostPurchaseRatingModal";
import { publishWalletUpdate } from "@/lib/walletBus";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderIdParam = params.get("order_id");
  const pendingApproval = params.get("pending") === "1";
  // Iteration 260 — auto-approved Piggy Bank purchases land here with
  // `approved=1` and render the instant-completion state.
  const autoApproved = params.get("approved") === "1";
  const kindParam = params.get("kind");
  const [state, setState] = useState({ kind: "pending" });
  const [ratingOpen, setRatingOpen] = useState(false);
  const [authed, setAuthed] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    // Iteration 232 — guest detection: only signed-in buyers get the
    // "View my orders" shortcut; guests get just "Continue Shopping".
    let live = true;
    api.get("/auth/me").then(() => { if (live) setAuthed(true); }).catch(() => { if (live) setAuthed(false); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    // Iteration 200 — unified confirmation for internal Kid-Piggy-Bank
    // purchases (Parent Handover / School Gate paid from balance).
    // These arrive at /order-confirmation right after the kid taps
    // buy, but the actual balance move waits on the parent tapping
    // approve. Render the page in a friendly "awaiting approval" state
    // rather than polling Stripe (there's no session to poll).
    if (pendingApproval || kindParam === "piggy") {
      setState({
        kind: autoApproved ? "auto_approved" : "pending_approval",
        data: {},
        orderInfo: { order_id: orderIdParam, id: orderIdParam },
      });
      return;
    }
    if (!sessionId) { setState({ kind: "error", msg: "Missing session id" }); return; }
    let cancelled = false;
    const startedAt = Date.now();
    const PAID_ORDER_STATUSES = ["paid", "escrow_held", "completed", "awaiting_shipment", "awaiting_posting", "shipped", "delivered", "collected"];
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        // Iteration 223 — verify-session actively retrieves the Stripe
        // session server-side, so a paid card flips instantly instead
        // of waiting on webhook lag ("Still processing" fallback).
        const { data } = await api.get(`/checkout/verify-session/${sessionId}`);
        // Iteration 231 — accept EVERY paid shape (web tier normalises
        // to payment_status/status; upstream answers with
        // stripe_payment_status/order_status). Without this the page
        // looped on "Confirming your payment…" forever.
        const ps = String(data?.payment_status || data?.stripe_payment_status || "").toLowerCase();
        const st = String(data?.status || "").toLowerCase();
        const os = String(data?.order_status || "").toLowerCase();
        const isPaid = ps === "paid" || st === "complete" || st === "paid" || PAID_ORDER_STATUSES.includes(os);
        if (isPaid) {
          let orderInfo = null;
          try { const r = await api.get(`/buyer/order-by-session/${sessionId}`); orderInfo = r.data; } catch (_) {}
          let receipt = null;
          try { const rr = await api.get(`/checkout/receipt/${sessionId}`); receipt = rr.data; } catch (_) {}
          setState({ kind: "paid", data, orderInfo, receipt });
          // Iteration 87 — broadcast so any open Kid Zone / Parent
          // Dashboard tab immediately refreshes the seller's passbook,
          // wallet, and Total Earnings without a reload.
          publishWalletUpdate({
            scope: "kid",
            reason: "sale_completed",
            kid_id: orderInfo?.seller_kid_id || receipt?.seller?.kid_id || null,
            order_id: orderInfo?.order_id || orderInfo?.id || receipt?.order_id || null,
          });
          return;
        }
        if (ps === "expired" || ps === "canceled" || st === "expired") { setState({ kind: "expired" }); return; }
        // Iteration 231/232 — fallback guard: once polling exceeds
        // 1.5s, force-fetch the receipt directly by session id and
        // render it instead of looping indefinitely.
        if (Date.now() - startedAt > 1500) {
          try {
            const rr = await api.get(`/checkout/receipt/${sessionId}`);
            if (rr.data && (rr.data.order_ref || rr.data.item_title)) {
              setState({ kind: "paid", data, orderInfo: null, receipt: rr.data });
              return;
            }
          } catch (_) { /* 409 while unconfirmed — keep polling */ }
        }
        if (attempts.current >= 8) { setState({ kind: "pending_long" }); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (Date.now() - startedAt > 1500) {
          try {
            const rr = await api.get(`/checkout/receipt/${sessionId}`);
            if (rr.data && (rr.data.order_ref || rr.data.item_title)) {
              setState({ kind: "paid", data: null, orderInfo: null, receipt: rr.data });
              return;
            }
          } catch (_) { /* keep polling */ }
        }
        if (attempts.current >= 8) { setState({ kind: "error", msg: "We couldn't verify payment. Check your email." }); return; }
        setTimeout(poll, 2500);
      }
    };
    poll();
    // Iteration 232 — independent 1.5s receipt fallback timer: buyers
    // must never sit on "Confirming your payment…" waiting for the next
    // 2.5s poll tick. Fires exactly once at 1.5s; a 409 (payment still
    // confirming) is ignored and the poll loop carries on.
    const fallbackTimer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const rr = await api.get(`/checkout/receipt/${sessionId}`);
        if (!cancelled && rr.data && (rr.data.order_ref || rr.data.item_title)) {
          setState((prev) => (prev.kind === "pending" ? { kind: "paid", data: null, orderInfo: null, receipt: rr.data } : prev));
        }
      } catch (_) { /* payment not confirmed yet — poll loop continues */ }
    }, 1500);
    return () => { cancelled = true; clearTimeout(fallbackTimer); };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        <div className="card-base p-10 text-center" data-testid="checkout-status-card">
          {state.kind === "pending" && (<>
            <Clock className="w-12 h-12 text-amber-500 mx-auto animate-pulse" />
            <h1 className="font-display text-3xl font-medium mt-4">Confirming your payment…</h1>
            <p className="text-slate-600 mt-2">This usually takes a couple of seconds.</p>
          </>)}
          {state.kind === "pending_approval" && (<>
            <Clock className="w-14 h-14 text-amber-500 mx-auto" />
            <h1 className="font-display text-3xl md:text-4xl font-semibold mt-4" data-testid="checkout-success-title">Thank You for Your Order!</h1>
            <p className="text-slate-600 mt-2">Your purchase has been sent to your parent for approval.</p>
            <div className="mt-6 max-w-md mx-auto text-left card-base p-5 bg-amber-50/60 border-amber-200" data-testid="checkout-next-steps-pending">
              <div className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">What happens next?</div>
              <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-5">
                <li>Your parent has <strong>24 hours</strong> to approve the purchase from their dashboard.</li>
                <li>Once approved, funds move from your Piggy Bank and the seller is notified with your <strong>handover PIN</strong>.</li>
                <li>You'll see the order in <strong>My Orders</strong> the moment approval lands.</li>
              </ul>
            </div>
            <div className="flex gap-3 justify-center mt-6 flex-wrap">
              <Link to="/browse" className="btn-primary" data-testid="checkout-back-to-browse-pending">Keep shopping</Link>
              <Link to="/orders" className="btn-secondary">View my orders</Link>
            </div>
          </>)}
          {state.kind === "auto_approved" && (<>
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h1 className="font-display text-3xl md:text-4xl font-semibold mt-4" data-testid="checkout-success-title">Purchase Complete!</h1>
            <p className="text-slate-600 mt-2">Your Piggy Bank purchase was auto-approved instantly — your parent has been notified.</p>
            <div className="mt-6 max-w-md mx-auto text-left card-base p-5 bg-emerald-50/60 border-emerald-200" data-testid="checkout-next-steps-auto-approved">
              <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">What happens next?</div>
              <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-5">
                <li>Funds have moved from your Piggy Bank and the seller&rsquo;s parent has been notified with the <strong>handover PIN</strong>.</li>
                <li>Your parent received an instant notification for full transparency.</li>
                <li>You&rsquo;ll see the order in <strong>My Orders</strong> right away.</li>
              </ul>
            </div>
            <div className="flex gap-3 justify-center mt-6 flex-wrap">
              <Link to="/browse" className="btn-primary" data-testid="checkout-back-to-browse-auto">Keep shopping</Link>
              <Link to="/orders" className="btn-secondary">View my orders</Link>
            </div>
          </>)}
          {state.kind === "paid" && (<>
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h1 className="font-display text-3xl md:text-4xl font-semibold mt-4" data-testid="checkout-success-title">Thank you for shopping at MyTableTop!</h1>
            <p className="text-slate-600 mt-2">A young entrepreneur just made a sale. <Sparkles className="inline w-4 h-4 text-amber-500" /></p>
            {state.receipt && (
              <div className="card-base p-6 mt-6 max-w-md mx-auto text-left" data-testid="checkout-receipt-card">
                <div className="flex items-start gap-4">
                  {state.receipt.photo_url && (
                    <img
                      src={state.receipt.photo_url}
                      alt={state.receipt.item_title || "Item photo"}
                      className="w-20 h-20 rounded-2xl object-cover border border-slate-200 flex-shrink-0"
                      data-testid="receipt-photo"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Receipt</div>
                    <div className="font-display text-lg text-slate-900 mt-0.5 leading-snug" data-testid="receipt-item-title">
                      {state.receipt.item_title || "Your item"}
                    </div>
                    {state.receipt.item_summary && (
                      <div className="text-xs text-slate-500 mt-0.5 leading-snug" data-testid="receipt-item-summary">{state.receipt.item_summary}</div>
                    )}
                    {state.receipt.seller?.handle && (
                      <div className="text-xs text-slate-500 mt-1" data-testid="receipt-seller-handle">
                        Seller: <span className="font-semibold text-slate-700">@{String(state.receipt.seller.handle).replace(/^@/, "")}</span>
                      </div>
                    )}
                    {state.receipt.order_ref && (
                      <div className="text-xs text-slate-500 mt-1">
                        Order Ref <span className="font-mono font-semibold text-slate-700">#{state.receipt.order_ref}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 text-sm" data-testid="receipt-breakdown">
                  {state.receipt.item_price_gbp && (
                    <div className="flex justify-between"><span className="text-slate-500">Item Price</span><span className="font-semibold text-slate-800">{state.receipt.item_price_gbp}</span></div>
                  )}
                  {state.receipt.postage_gbp && (
                    <div className="flex justify-between"><span className="text-slate-500">Royal Mail Postage</span><span className="font-semibold text-slate-800">{state.receipt.postage_gbp}</span></div>
                  )}
                  {state.receipt.platform_fee_gbp && (
                    <div className="flex justify-between"><span className="text-slate-500">Family Safety &amp; Buyer Protection Fee (10%)</span><span className="font-semibold text-slate-800">{state.receipt.platform_fee_gbp}</span></div>
                  )}
                  {state.receipt.stripe_fee_gbp && (
                    <div className="flex justify-between"><span className="text-slate-500">Stripe Payment Processing Fee (1.5% + 20p)</span><span className="font-semibold text-slate-800">{state.receipt.stripe_fee_gbp}</span></div>
                  )}
                  {state.receipt.total_gbp && (
                    <div className="flex justify-between pt-1.5 border-t border-slate-100"><span className="font-bold text-slate-900">Total Paid</span><span className="font-bold text-brandTeal text-base">{state.receipt.total_gbp}</span></div>
                  )}
                </div>
                {(state.receipt.delivery_address || state.receipt.shipping_status) && (
                  <div className="mt-4 pt-4 border-t border-slate-100" data-testid="receipt-delivery">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Delivery</div>
                      {state.receipt.shipping_status && (
                        <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5" data-testid="receipt-shipping-status">
                          {state.receipt.shipping_status}
                        </span>
                      )}
                    </div>
                    {state.receipt.delivery_address && (
                      <div className="text-sm text-slate-700 mt-1.5 leading-snug">
                        {state.receipt.recipient_name && <div className="font-semibold">{state.receipt.recipient_name}</div>}
                        <div>{[state.receipt.delivery_address.line1, state.receipt.delivery_address.line2].filter(Boolean).join(", ")}</div>
                        <div>{[state.receipt.delivery_address.city, state.receipt.delivery_address.postcode].filter(Boolean).join(", ")}</div>
                      </div>
                    )}
                    {state.receipt.tracking_number && (
                      <div className="text-xs text-slate-500 mt-1.5">Tracking: <span className="font-mono font-semibold">{state.receipt.tracking_number}</span></div>
                    )}
                  </div>
                )}
              </div>
            )}
            {!state.receipt && state.orderInfo?.amount && (
              <div className="card-base p-5 mt-6 max-w-sm mx-auto text-left" data-testid="checkout-order-summary">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Order summary</div>
                <div className="font-display text-lg text-slate-900 mt-1">{state.orderInfo.title || "Listing"}</div>
                <div className="text-2xl font-display font-semibold text-brandTeal mt-1">{formatGBP(state.orderInfo.amount)}</div>
                {state.orderInfo.order_ref && (
                  <div className="text-xs text-slate-500 mt-2">
                    Order Ref <span className="font-mono font-semibold text-slate-700">#{state.orderInfo.order_ref}</span>
                  </div>
                )}
                {state.orderInfo.collection_code && (
                  <div className="text-sm text-slate-700 mt-2">
                    <strong>Handover PIN:</strong> <span className="font-mono tracking-widest">{state.orderInfo.collection_code}</span>
                  </div>
                )}
              </div>
            )}
            <div className="mt-6 max-w-md mx-auto text-left card-base p-5 bg-amber-50/60 border-amber-200" data-testid="checkout-next-steps">
              <div className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">Next Steps</div>
              {(() => {
                // Iteration 232 — postal orders get dispatch copy, not
                // meet-up / handover-PIN instructions.
                const fm = state.receipt?.fulfillment_method || state.orderInfo?.fulfillment_method || "";
                const isPostal = ["shipping", "home_delivery"].includes(fm);
                return isPostal ? (
                  <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-5" data-testid="next-steps-postal">
                    <li>Check your inbox for your confirmation receipt.</li>
                    <li>The seller&rsquo;s parent has been notified to post your item via Royal Mail.</li>
                    <li>You will receive an email update with tracking details as soon as it is posted.</li>
                  </ul>
                ) : (
                  <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-5" data-testid="next-steps-handover">
                    <li>Check your inbox for your receipt and (if a handover) your <strong>4-digit PIN</strong>.</li>
                    <li>The seller&rsquo;s parent will use the buyer contact you shared to arrange the meet-up.</li>
                    <li>Ratings unlock once your item arrives — help the community!</li>
                  </ul>
                );
              })()}
            </div>
            <div className="flex gap-3 justify-center mt-6 flex-wrap">
              <Link to="/browse" className="btn-primary" data-testid="checkout-continue-shopping-btn">Continue Shopping</Link>
              {authed === true && (
                <Link to="/orders" className="btn-secondary" data-testid="checkout-view-orders-btn">View my orders</Link>
              )}
              {(state.orderInfo?.order_id || state.orderInfo?.id) && state.orderInfo?.seller_kid_id && (
                <button
                  type="button"
                  onClick={() => setRatingOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm shadow-sm transition"
                  data-testid="checkout-rate-seller-btn"
                >
                  <Star className="w-4 h-4 fill-white" /> Rate {state.orderInfo?.seller_name || "seller"}
                </button>
              )}
            </div>
          </>)}
          {state.kind === "expired" && (<>
            <XCircle className="w-12 h-12 text-rose-500 mx-auto" />
            <h1 className="font-display text-3xl font-medium mt-4">Checkout expired</h1>
            <p className="text-slate-600 mt-2">No charge was made. Try again from the listing.</p>
            <Link to="/browse" className="btn-primary mt-6 inline-flex">Back to marketplace</Link>
          </>)}
          {state.kind === "pending_long" && (<>
            <Clock className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="font-display text-3xl font-medium mt-4">Still processing</h1>
            <p className="text-slate-600 mt-2">Your payment is taking a bit longer. We'll email you when it's complete.</p>
            <Link to="/browse" className="btn-primary mt-6 inline-flex">Back to marketplace</Link>
          </>)}
          {state.kind === "error" && (<>
            <XCircle className="w-12 h-12 text-rose-500 mx-auto" />
            <h1 className="font-display text-3xl font-medium mt-4">{state.msg}</h1>
            <Link to="/browse" className="btn-primary mt-6 inline-flex">Back to marketplace</Link>
          </>)}
        </div>
      </section>
      {(state.orderInfo?.order_id || state.orderInfo?.id || state.receipt?.order_id) && (state.orderInfo?.seller_kid_id || state.receipt?.seller?.kid_id) && (
        <PostPurchaseRatingModal
          open={ratingOpen}
          onOpenChange={setRatingOpen}
          orderId={state.orderInfo?.order_id || state.orderInfo?.id || state.receipt?.order_id}
          sellerKidId={state.orderInfo?.seller_kid_id || state.receipt?.seller?.kid_id}
          sellerName={state.orderInfo?.seller_name || state.receipt?.seller?.handle || "the seller"}
        />
      )}
    </div>
  );
}
